import statisticSvc from "../../services/api/statistic"
import { useState, useEffect } from "react"
import { notify } from "../../common/notificationService"
import { useLookupStation } from "../../utils/get-lookup"
import dayjs from "dayjs";
import Select from 'react-select';
import { listStatisticStatus, listRangeDate, listExportType } from "../../utils/helper";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateRangePicker } from "@mui/x-date-pickers-pro";
import { StatisticMinMaxDataItem } from "../../models/types/statistic";
import StatisticMinMaxTable from "./table";
import ReactSelectComponent from "../../components/react-select";
import { Download } from "lucide-react";
import { useResponsive } from "../../hooks/mediaQuery";
import { useTranslation } from "react-i18next";

const StatisticMinMaxPage = () => {
    const { lookupStation } = useLookupStation();
    const { isXs } = useResponsive();
    const { t } = useTranslation()
    const [filterParams, setFilterParams] = useState<any>({
        // stationId: 9,
        page: 1,
        limit: 50,
        status: listStatisticStatus.map((item: any) => item.value),
        from: listRangeDate[0].from,
        to: listRangeDate[0].to,
    });
    const [rangeDate, setRangeDate] = useState<any>(listRangeDate[0].value);
    const [exportType, setExportType] = useState<'excel' | 'pdf'>('excel');
    const [staticData, setStaticData] = useState<StatisticMinMaxDataItem[] | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const fetchData = async (params: any) => {
        setLoading(true);
        try {
            const response = await statisticSvc.getMinMaxParameterData(params);
            if (response) {
                if (response.content) {
                    setStaticData(response.content);
                } else {
                    setStaticData(null);
                }
            }
        } catch (error) {
            console.error(error);
            notify("Lỗi", "Xảy ra lỗi", "danger");
        } finally {
            setLoading(false);
        }
    }

    const handleExport = async () => {
        try {
            const response = await statisticSvc.exportMinMaxParameterData({ ...filterParams, fileType: exportType });
            if (response) {
                const url = window.URL.createObjectURL(new Blob([response]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'statistic_min-max_data_(' + dayjs(filterParams.from).format('DD-MM-YYYY') + '_' + dayjs(filterParams.to).format('DD-MM-YYYY') + ')' + (exportType === 'excel' ? '.xlsx' : '.pdf'));
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (error) {
            console.error(error);
            notify(t("common.error"), t("common.errorMessage"), "danger");
        }
    }

    useEffect(() => {
        let params = filterParams;
        if (params.stationId && params.status.length > 0 && params.from && params.to) {
            fetchData(params);
        }
        console.log(filterParams);
    }, [filterParams]);

    const handleChangeWMParams = (params: any) => {
        setFilterParams({ ...filterParams, ...params });
    }

    const handleDateChange = (params: any) => {
        if (params[0] && params[1]) {
            setFilterParams({ ...filterParams, from: dayjs(params[0]).format('YYYY-MM-DD HH:mm'), to: dayjs(params[1]).add(1439, 'minute').format('YYYY-MM-DD HH:mm') });
        }
    }

    return (
        <div className="page-wrapper h-full w-full">
            <div className="h-full flex flex-col">
                <div className={`rounded-lg border border-gray-200 shadow-sm bg-white ${isXs ? 'p-2' : 'p-4'} mb-2`}>
                    <div className={`flex flex-wrap items-center ${isXs ? 'gap-2' : 'gap-4'}`}>
                        <div className="min-w-[250px]">
                            <label className="block text-sm font-medium text-gray-700">{t('statisticData.station')} <span className="text-red-500">*</span></label>
                            <Select
                                className="w-full"
                                options={lookupStation}
                                placeholder={t('statisticData.selectStation')}
                                value={lookupStation.find(option => option.value === filterParams.stationId)}
                                onChange={(e: any) => handleChangeWMParams({ stationId: e.value })}
                                isSearchable={false}
                            />
                        </div>
                        <div className="min-w-[250px]">
                            <label className="block text-sm font-medium text-gray-700">{t('statisticData.dataType')} <span className="text-red-500">*</span></label>
                            <ReactSelectComponent
                                options={
                                    listStatisticStatus.length === filterParams.status.length ?
                                        listStatisticStatus : [{ label: t('statisticData.selectAll'), value: '*' }, ...listStatisticStatus]
                                }
                                value={filterParams.status.map((item: any) => listStatisticStatus.find((option: any) => option.value === item))}
                                onChange={(selected: any) => {
                                    if (!selected || selected.length === 0) {
                                        setFilterParams({ ...filterParams, status: [] });
                                        return;
                                    }
                                    if (selected.some((option: any) => option.value === '*')) {
                                        setFilterParams({ ...filterParams, status: listStatisticStatus.map((item: any) => item.value) });
                                    } else {
                                        setFilterParams({ ...filterParams, status: selected.map((item: any) => item.value) });
                                    }
                                }}
                                isMulti
                                isDisabled={!filterParams.stationId}
                                noOptionsMessage={t('statisticData.allParametersSelected')}
                                placeholder={t('statisticData.selectParameter')}
                            />
                        </div>
                        <div className="flex flex-wrap items-center gap-4">
                            <div className="min-w-[250px]">
                                <label className="block text-sm font-medium text-gray-700">{t('statisticData.timeRange')} <span className="text-red-500">*</span></label>
                                <Select
                                    className="w-full"
                                    options={listRangeDate}
                                    placeholder={t('statisticData.selectTimeRange')}
                                    isDisabled={!filterParams.stationId}
                                    value={listRangeDate.find(option => option.value === rangeDate)}
                                    onChange={(e: any) => {
                                        handleChangeWMParams({ from: e.from, to: e.to });
                                        setRangeDate(e.value);
                                    }}
                                    isSearchable={false}
                                />
                            </div>
                            {rangeDate === 4 && (
                                <div className="min-w-[290px]">
                                    <label className="block text-sm font-medium text-gray-700">Khoảng thời gian <span className="text-red-500">*</span></label>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DateRangePicker
                                            format="DD/MM/YYYY"
                                            className="date-range-picker w-full"
                                            onChange={(e: any) => handleDateChange(e)}
                                        />
                                    </LocalizationProvider>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className={`flex-1 flex overflow-y-auto flex-col rounded-lg border border-gray-200 shadow-sm bg-white ${isXs ? 'px-2' : 'px-4'}`}>
                    <div className="h-full w-full">
                        {staticData ? (
                            <>
                                <div className="bg-white flex flex-wrap items-center justify-between gap-4 mt-2">
                                    <div className="flex flex-wrap items-center gap-4">
                                        <div className={`flex items-center align-center border border-gray-300 rounded ${isXs ? 'text-xs' : 'text-base'}`}>
                                            <div
                                                className={`flex items-center align-center font-medium text-gray-700 p-2 bg-gray-100 cursor-pointer hover:bg-gray-200 ${isXs ? 'text-xs' : 'text-base'}`}
                                                onClick={() => handleExport()}
                                            >
                                                <Download size={isXs ? 14 : 16} className="mr-2" />{t('statisticData.export')}
                                            </div>
                                            <Select
                                                className="w-full"
                                                options={listExportType}
                                                placeholder={t('statisticData.selectExportType')}
                                                value={listExportType.find(option => option.value === exportType)}
                                                onChange={(e: any) => setExportType(e.value)}
                                                isSearchable={false}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="flex-1 overflow-y-auto mt-3">
                                    <StatisticMinMaxTable staticData={staticData} loading={loading} />
                                </div>
                            </>
                        ) : !filterParams.stationId ? (
                            <div className="flex items-center justify-center h-full">
                                <div className="text-gray-600">{t('statisticData.selectStationToShowData')}</div>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center h-full">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default StatisticMinMaxPage
